import axios from "axios";
import { BASE_URL } from "common/config/baseUrl";
import { activeToken, onHub, onHubConnected } from "./taskHub";

export type TaskStatusEvent = {
  seq: number;
  eventType: 0 | 1 | 2;
  eventTypeName: "StatusChanged" | "TaskCreated" | "AssigneesChanged";
  detail?: string | null;                   // TaskCreated / AssigneesChanged only
  taskId: number; displayId?: string | null; taskName: string;
  projectId?: number | null; projectName?: string | null;
  fromStatus: number; fromStatusName: string; toStatus: number; toStatusName: string;
  actorEmployeeId?: string | null;          // null when an admin / supervisor made the change
  actorUserId?: number | null;
  actorRole: "Employee" | "Admin" | "Supervisor";
  actorName?: string | null; reason?: string | null;
  actorOrgName?: string | null;             // only on shared projects
  isShared: boolean;                        // the task's project is shared between two organisations
  ownerOrgName?: string | null;             // only on shared projects
  message: string; createdAt: string;
};

/** "Ann", or "Ann (Akij iBOS)" on a shared project; a generic label when the name is missing. */
type Translate = (key: string, options?: Record<string, unknown>) => unknown;

export const actorLabel = (ev: TaskStatusEvent, t: Translate): string => {
  const who = ev.actorName
    ?? String(t(ev.actorRole === "Employee" ? "layout:taskStatus.anEmployee" : "layout:taskStatus.anAdmin"));
  return ev.isShared && ev.actorOrgName ? `${who} (${ev.actorOrgName})` : who;
};

/** The second line of a toast / bell row, for every event type. */
export const eventText = (ev: TaskStatusEvent, t: Translate): string => {
  const who = actorLabel(ev, t);
  const detail = ev.detail ? ` — ${ev.detail}` : "";
  switch (ev.eventTypeName) {
    case "TaskCreated":
      return String(t("layout:taskStatus.created", { actor: who })) + detail;
    case "AssigneesChanged":
      return String(t("layout:taskStatus.assigneesChanged", { actor: who })) + detail;
    default:
      return `${who}: ${ev.fromStatusName} → ${ev.toStatusName}`;
  }
};
export type TaskStatusPage = { items: TaskStatusEvent[]; hasMore: boolean; unreadCount: number; lastReadSeq: number };
export type TaskStatusUnread = { unreadCount: number; lastReadSeq: number };

type Listener = (events: TaskStatusEvent[], meta: { replay: boolean }) => void;

const http = () => axios.create({ baseURL: BASE_URL, headers: { Authorization: `Bearer ${activeToken()}` } });

/** REST for the bell panel. */
export const taskStatusApi = {
  recent: async (beforeSeq?: number, pageSize = 30): Promise<TaskStatusPage> =>
    (await http().get("/api/TaskStatusEvent/recent", { params: { beforeSeq, pageSize } })).data,
  unread: async (): Promise<TaskStatusUnread> =>
    (await http().get("/api/TaskStatusEvent/unread-count")).data,
  markRead: async (upToSeq: number): Promise<TaskStatusUnread> =>
    (await http().post("/api/TaskStatusEvent/read", { upToSeq })).data,
};

const listeners = new Set<Listener>();
let active = false;
let generation = 0;
let cursor = -1;                 // complete up to here; -1 = not known yet
let cursorKey = "";
let syncing: Promise<void> | null = null;

export function onTaskStatusEvents(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function emit(events: TaskStatusEvent[], replay: boolean) {
  if (events.length === 0) return;
  listeners.forEach((fn) => { try { fn(events, { replay }); } catch { /* one bad listener must not stop others */ } });
}

function saveCursor() {
  try { localStorage.setItem(cursorKey, String(cursor)); } catch { /* private mode */ }
}

/** First visit on this browser: start at "now", not 90 days back. */
async function initCursor(gen: number) {
  try {
    const page = await taskStatusApi.recent(undefined, 1);
    if (gen !== generation) return;
    cursor = page.items?.[0]?.seq ?? 0;
    saveCursor();
  } catch { /* stays -1: the next sync tries again */ }
}

/** Replay everything after the cursor. Only one replay runs at a time. */
function sync(): Promise<void> {
  if (syncing) return syncing;
  const gen = generation;
  const run = (async () => {
    try {
      if (cursor < 0) {
        await initCursor(gen);
        if (cursor < 0) return;
      }
      for (;;) {
        const { data } = await http().get("/api/TaskStatusEvent/stream", { params: { afterSeq: cursor, limit: 200 } });
        if (gen !== generation) return;             // stopped / user switched meanwhile
        emit(data.items ?? [], true);
        const through = Number(data.throughSeq);
        if (Number.isFinite(through)) cursor = through; // may go DOWN after a DB restore: intended
        saveCursor();
        if (!data.hasMore) break;
      }
    } catch { /* offline / 401: the next reconnect or push retries */ }
  })();
  syncing = run;
  run.finally(() => { if (syncing === run) syncing = null; });
  return run;
}

async function onPush(ev: TaskStatusEvent) {
  if (!active) return;
  if (syncing) await syncing;                     // let a running replay finish first
  if (cursor >= 0 && ev.seq <= cursor) return;    // already have it
  if (cursor >= 0 && ev.seq === cursor + 1) {     // exactly the next one: apply directly
    cursor = ev.seq;
    saveCursor();
    emit([ev], false);
    return;
  }
  await sync();                                   // a jump (or no cursor yet): replay
}

// Registered once, for the life of the app.
onHub("TaskStatusChanged", (ev: TaskStatusEvent) => { onPush(ev); });
onHubConnected(() => { if (active) sync(); });    // catch up after every (re)connect

/** userKey: unique per viewer, e.g. `${accountid}:${userid}` from tokenData. */
export function startTaskStatusFeed(userKey: string) {
  generation++;
  active = true;
  syncing = null;
  cursorKey = `tf.taskStatus.cursor.${userKey}`;
  cursor = -1;
  try {
    const raw = localStorage.getItem(cursorKey);
    const saved = Number(raw);
    if (raw !== null && Number.isFinite(saved) && saved >= 0) cursor = saved;
  } catch { /* ignore */ }
  sync();                                         // catch up from where this browser left off
}

export function stopTaskStatusFeed() {
  generation++;
  active = false;
  syncing = null;
}
